import { useState } from 'react';
import { useSelector } from 'react-redux';

import {
  CareEnvironment,
  CarePhase,
  careEnvironmentDefs,
  carePhaseDefs,
  carePhaseDefList,
  pickClientCareEnvironmentCosts,
} from '@shared';
import { selectClient } from '@/model';
import {
  getPhaseInflationSchedule,
  PhaseInflationScheduleRow,
} from '@/util/getPhaseInflationSchedule';

export interface CarePhasesSummaryData {
  id: number;
  phaseLabel: string;
  phaseInflatedProfessionalShareCost: number;
  phaseInflatedRoomAndBoardCost: number;
  phaseInflatedTotalCost: number;
  careEnvironment: CareEnvironment;
  careEnvironmentLabel: string;
  rateAmount: number;
  homeRateAmount: number;
  hasCustomRate: boolean;
  rateLocaleDescription: string;
  phaseCareMonthsNeeded: number;
  phaseProfessionalCareHoursProvided: number;
  phaseInflationFactor: number | null;
  inHomeEnvironment: boolean;
  // Independent Living under the claim-projection engine splits into
  // home-style care (policy-eligible) plus out-of-pocket room & board; 0 for
  // every other environment / on the legacy engine, so the itemized display
  // only activates for an IL phase under the new engine.
  hasRoomAndBoard: boolean;
  yearsTillPhaseEnd: number | null;
  isDurationCustom: boolean;
  inflationSchedule: PhaseInflationScheduleRow[];
}

export function useCarePhasesSummarySlide() {
  const client = useSelector(selectClient)!;

  const {
    phaseCosts,
    appliedCareEnvironments,
    clientPhaseInflationFactors,
    clientYearsTillPhaseEnd,
    allPhaseCosts: { allPhaseInflatedProfessionalShareCost, allPhaseInflatedRoomAndBoardCost },
    clientCalculationSettings: { healthCareInflationRate, useClaimProjectionEngine },
  } = client;

  const homeRateAmount = pickClientCareEnvironmentCosts(client, CareEnvironment.home).rateAmount;

  // With the claim-projection engine on, phase costs (and the growth factor
  // above) are priced month by month, so the factor is the month-weighted
  // AVERAGE of annually-stepped multipliers — the label drops the year count
  // and an info tooltip presents the per-month schedule instead.
  const useMonthBasedGrowth = useClaimProjectionEngine === true;

  const [openEditCostModal, setOpenEditCostModal] = useState(false);
  const [openEditInflationModal, setOpenEditInflationModal] = useState(false);
  const [selectedCareEnvironment, setSelectedCareEnvironment] = useState<CareEnvironment | null>(
    null,
  );

  const handleEditCostClick = (careEnvironment: CareEnvironment) => {
    setSelectedCareEnvironment(careEnvironment);
    setOpenEditCostModal(true);
  };

  const handleEditInflationClick = () => {
    setOpenEditInflationModal(true);
  };

  const carePhaseData: CarePhasesSummaryData[] = carePhaseDefList.map(phase => {
    const singlePhaseCosts = phaseCosts[phase.index];
    const phaseLabel = carePhaseDefs[phase.value].label;
    const yearsTillPhaseEnd = clientYearsTillPhaseEnd[phase.value];
    const {
      inHomeEnvironment,
      phaseInflatedProfessionalShareCost,
      phaseInflatedRoomAndBoardCost,
      phaseRoomAndBoardCost,
      phaseCareMonthsNeeded,
      phaseProfessionalCareHoursProvided,
    } = singlePhaseCosts;
    const hasRoomAndBoard = (phaseRoomAndBoardCost ?? 0) > 0;
    const phaseInflationFactor = clientPhaseInflationFactors[phase.value];
    const careEnvironment = appliedCareEnvironments[phase.value];
    const careEnvironmentLabel = careEnvironmentDefs[careEnvironment].label;
    const isDurationCustom = singlePhaseCosts.isDurationCustom;
    const { rateAmount, hasCustomRate, rateLocaleDescription } = pickClientCareEnvironmentCosts(
      client,
      careEnvironment,
    );

    return {
      id: phase.index,
      phaseLabel,
      phaseInflatedProfessionalShareCost,
      phaseInflatedRoomAndBoardCost,
      phaseInflatedTotalCost: phaseInflatedProfessionalShareCost + phaseInflatedRoomAndBoardCost,
      careEnvironment,
      careEnvironmentLabel,
      rateAmount,
      homeRateAmount,
      hasCustomRate,
      rateLocaleDescription,
      phaseCareMonthsNeeded,
      phaseProfessionalCareHoursProvided,
      phaseInflationFactor,
      inHomeEnvironment,
      hasRoomAndBoard,
      yearsTillPhaseEnd,
      isDurationCustom,
      inflationSchedule: useMonthBasedGrowth
        ? getPhaseInflationSchedule(client, phase.value)
        : [],
    };
  });

  const visibleCarePhaseData = carePhaseData.filter(phase => Boolean(phase.yearsTillPhaseEnd));

  // For useSlideSetup previousSlidePage computation
  const isFullCareHome = appliedCareEnvironments[CarePhase.fullCare] === CareEnvironment.home;

  return {
    client,
    allPhaseInflatedProfessionalShareCost,
    allPhaseInflatedRoomAndBoardCost,
    healthCareInflationRate,
    useMonthBasedGrowth,
    visibleCarePhaseData,
    isFullCareHome,
    openEditCostModal,
    setOpenEditCostModal,
    openEditInflationModal,
    setOpenEditInflationModal,
    selectedCareEnvironment,
    handleEditCostClick,
    handleEditInflationClick,
  };
}
